import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

// Every factual claim maps to the source map in
// docs/ci-research-chatgpt-ads-conversions-campaign/FINDINGS-2026-10-17.md
// (OpenAI help + developer docs, plus first-hand Ads Manager captures).

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const AMBER = "#E0B057";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const CHECKER = "/tools/chatgpt-ads-pixel-checker";
const HUB = "/blog/chatgpt-ads-conversion-tracking";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: 19,
        color: TEXT,
        margin: "28px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontSize: 16, lineHeight: 1.75, color: TEXT_DIM, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 10 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}44` }}
      {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        background: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: 4,
        fontFamily: "var(--mono)",
        fontSize: 13,
        color: TEXT,
      }}
    >
      {children}
    </code>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        background: "rgba(79,205,196,0.05)",
        borderRadius: "0 12px 12px 0",
        fontFamily: "var(--display)",
        fontSize: 19,
        lineHeight: 1.5,
        color: TEXT,
        fontWeight: 500,
      }}
    >
      {children}
    </blockquote>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "36px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: "rgba(79,205,196,0.06)",
        border: "1px solid rgba(79,205,196,0.2)",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 14 }}>{children}</div>
      <a
        href={href}
        style={{
          display: "inline-block",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          minHeight: 44,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

const th = (accent) => ({
  textAlign: "left",
  padding: "12px 14px",
  color: accent ? TEAL : TEXT,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontFamily: "var(--mono)",
});
const td = (strong) => ({
  padding: "13px 14px",
  color: strong ? TEXT : TEXT_DIM,
  fontWeight: strong ? 600 : 400,
  verticalAlign: "top",
});

function DataTable({ title, note, headers, rows, minWidth = 680 }) {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>{title}</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>{note}</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
              {headers.map((h, i) => (
                <th key={h} scope="col" style={th(i === 0)}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row[0]} style={{ borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                {row.map((cell, j) => (
                  <td key={j} style={td(j === 0)}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Visual: what locks at launch and what does not ── */
function LockedAtLaunch() {
  const locked = [
    ["Objective", "Cannot change in place. Clone or start a new campaign."],
    ["How you're charged", "Clicks to Impressions: clone the campaign. Anything else: a new campaign."],
    ["Conversion event", "One per campaign, fixed. A different event means a new campaign."],
  ];
  const open = [
    ["Bid cap and bid strategy", "OpenAI suggests adjusting these as results come in."],
    ["Budget and creative", "OpenAI advises waiting for enough volume before large changes."],
  ];
  const card = (label, color, items) => (
    <div style={{ background: "#0f0f11", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--mono)", marginBottom: 14 }}>{label}</div>
      {items.map(([name, note]) => (
        <div key={name} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: TEXT, fontFamily: "var(--body)" }}>{name}</div>
          <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.55, fontFamily: "var(--body)" }}>{note}</div>
        </div>
      ))}
    </div>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, margin: "16px 0 32px" }}>
      {card("Locked at launch", AMBER, locked)}
      {card("Adjustable later", TEAL, open)}
    </div>
  );
}

/* ── Visual: bid cap math for a hypothetical AI marketing platform ── */
function BidCapMath() {
  const rows = [
    ["Annual contract value", "$12,000"],
    ["Booked demos that become qualified opportunities", "30%"],
    ["Opportunities that close", "25%"],
    ["First-year revenue per booked demo", "$12,000 × 30% × 25% = $900"],
    ["Most it will spend in ads to win one customer", "$4,000 (a third of year one)"],
    ["Most it can pay per booked demo", "$4,000 × 30% × 25% = $300"],
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>Setting a bid cap from your own numbers</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>A hypothetical AI marketing platform sold on annual contracts; swap in your own funnel rates.</div>
      {rows.map(([label, value], i) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", padding: "10px 0", borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontSize: 14, color: TEXT_DIM, fontFamily: "var(--body)" }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: i === rows.length - 1 ? TEAL : TEXT, fontFamily: "var(--mono)" }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

const eventRows = [
  ["Page viewed", "A user lands on or views an important page.", "Avoid as a goal: it optimizes toward visits, not buyers.", "Highest"],
  ["Registration completed", "A user finishes an account or event registration flow.", "Webinar or free-account sign-ups, when registration starts your funnel.", "High"],
  ["Lead created", "A user submits a lead form or requests contact.", "Contact and content forms. The safe choice when demos are scarce.", "Medium"],
  ["Trial started", "A free trial starts.", "Product-led companies with a free trial.", "Medium"],
  ["Appointment scheduled", "A user books a meeting, demo, or consultation.", "Sales-led companies. The standard event closest to pipeline.", "Low"],
  ["Subscription created", "A paid subscription starts.", "Only for self-serve plans that sell often.", "Lowest"],
];

const billingRows = [
  ["You pay for", "Impressions served", "Valid clicks"],
  ["Delivery optimizes toward", "Conversions after an ad view or a click", "Conversions after a click"],
  ["Main cost metric", "Average CPM and cost per conversion", "Average CPC"],
  ["In Ads Manager", "Pre-selected when we set one up", "One click away"],
  ["Changing later", "Moving to clicks means a new campaign", "Clone to move to impressions"],
  ["Best B2B use", "Once you trust the event and want volume", "While you build a baseline your CRM can verify"],
];

const faqs = [
  {
    q: "Do you pay per conversion in a ChatGPT Ads Conversions campaign?",
    a: "No. OpenAI states plainly that the Conversions objective does not mean you pay per conversion. The campaign optimizes delivery toward your chosen conversion event, but you are billed per impression or per valid click, depending on the option you pick. Your bid cap is not a price either: the auction sets your actual cost per conversion.",
  },
  {
    q: "Why is impression billing the default for ChatGPT Ads conversion campaigns?",
    a: "OpenAI's position is that impressions find more conversions, because people may see an ad, leave, and convert later without clicking. Its help center says selecting Clicks will lower your conversions. Ads Manager pre-selected Impressions (CPM) when we set up a Conversions campaign on October 17, 2026. Campaigns created through OpenAI's Ads API bill Conversions per click.",
  },
  {
    q: "What conversion event should a B2B SaaS company optimize for in ChatGPT Ads?",
    a: "The deepest standard event that still fires often. For a sales-led company that is usually Appointment scheduled, the event OpenAI defines as booking a meeting, demo, or consultation. For a product-led company it is usually Trial started. If either is rare, step back to Lead created. Track the whole funnel, but each campaign optimizes toward one event.",
  },
  {
    q: "Can you change the conversion event after a ChatGPT Ads campaign launches?",
    a: "No. OpenAI states that the conversion event cannot be changed after campaign creation, and neither can the objective or the billing option. To move a campaign from click billing to impression billing, clone it and choose Impressions. To optimize toward a different event, create a new campaign. Choose the event before you build anything else.",
  },
  {
    q: "How many conversions does a ChatGPT Ads Conversions campaign need?",
    a: "OpenAI does not publish a number. Its guidance is only that events which happen too rarely make performance harder to evaluate. For a sense of scale, Meta says its ad sets usually leave the learning phase after about 50 results in a week. That is Meta's rule, not OpenAI's, but it shows why a handful of demos a month is thin.",
  },
  {
    q: "Why can't I select my conversion event in a ChatGPT Ads campaign?",
    a: "Usually one of three reasons. The event is custom, and OpenAI does not support custom events for the Conversions objective. The event is firing but has not been set up as a conversion event in Ads Manager, so it is not in the campaign's list. Or no pixel or Conversions API data source is sending it yet.",
  },
];

const howToSteps = [
  ["Confirm your conversion event reaches OpenAI", "Make sure a pixel or Conversions API data source is sending a standard event, and that the event is set up as a conversion in Ads Manager."],
  ["Create a campaign and choose Conversions", "Start a new campaign in Ads Manager, or clone an existing one, and set the Objective to Conversions."],
  ["Pick the conversion event", "Choose the one standard event the campaign will optimize toward. It cannot be changed after the campaign is created."],
  ["Choose how you're charged", "Select Impressions (CPM) or Clicks (CPC). The billing option cannot be changed in place later."],
  ["Set locations, budget and dates", "Choose markets, a daily budget and a schedule that give the campaign enough time to gather conversions."],
  ["Build the ad group and set the bid", "Choose Autobid or Manual: Max bid, set a conversion-optimized bid cap from your own numbers, and add tracking parameters."],
  ["Add ads, review and launch", "Create the ads, review every setting, and launch. Drafts do not keep campaign budgets."],
];

function Content() {
  return (
    <article>
      {/* ── Overview box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            To set up a ChatGPT Ads conversion campaign, create a campaign with the Conversions objective, pick one standard conversion event, choose whether you are billed per impression or per click, and set a bid on the ad group. For B2B, the event and the billing option matter most, because neither can change after launch.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li><Strong>Never per conversion:</Strong> you pay per impression or per click; the auction sets your cost per conversion</Li>
          <Li><Strong>Billing:</Strong> Impressions (CPM) was pre-selected in Ads Manager; Clicks (CPC) is the alternative</Li>
          <Li><Strong>One event, locked:</Strong> a standard event only, and it cannot be swapped later</Li>
          <Li><Strong>B2B pick:</Strong> the deepest event that still fires often, usually a booked demo or a started trial</Li>
          <Li><Strong>No recommended bid:</Strong> OpenAI publishes none, so set your bid cap from your own funnel</Li>
        </ul>
      </div>

      <H2 id="what-you-pay-for">What a conversion campaign does, and what you pay for</H2>
      <P>
        <Strong>A conversion campaign is a <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> campaign that steers delivery toward one action on your site, such as a booked demo.</Strong> It changes who the system shows your ads to. It does not change what you pay for: you are billed per impression or per valid click, never per conversion.
      </P>
      <P>
        OpenAI is unusually direct about this. Its <A href="https://help.openai.com/en/articles/20001412-create-ocpc-campaigns">conversion campaign guide</A> asks itself whether the objective means you pay per conversion, and answers with one word: no. So the familiar <A href="/glossary/cost-per-action">cost-per-action</A> model from other ad platforms does not apply. Cost per conversion is something you calculate afterwards (spend divided by conversions), not something you buy.
      </P>
      <P>
        One wrinkle matters if a tool builds your campaigns. OpenAI&#39;s Ads API documentation pairs the Conversions objective only with click billing: <A href="https://developers.openai.com/ads/bidding-and-budgets">its bidding guide</A> says conversion campaigns &ldquo;bill for valid clicks.&rdquo; Impression billing is a choice you make in Ads Manager itself. If a tool builds your campaigns through the API, and that includes the <A href="/platform/ai-ads">AI Ads module in AI-Advisors</A>, expect click billing on Conversions campaigns today.
      </P>
      <PullQuote>
        A conversion campaign changes what the system aims at, not what you pay for. You still buy impressions or clicks.
      </PullQuote>

      <H2 id="before-you-start">Before you start: what must already be true</H2>
      <P>
        <Strong>A Conversions campaign can only optimize toward an event OpenAI already receives.</Strong> OpenAI lists four conditions: conversion tracking is set up through its pixel or Conversions API, at least one standard event is available, you know which event you want, and you are ready to create a new campaign, or clone an old one, rather than switch an existing campaign in place.
      </P>
      <P>
        Two details trip up B2B teams. First, <Strong>custom events are not eligible.</Strong> OpenAI&#39;s help center says custom conversion events are not currently supported for this objective, so a <Code>custom</Code> event called <Code>demo_request</Code> will not do; it has to be a standard event such as <Code>appointment_scheduled</Code>. Second, an event only appears in the campaign once it is set up as a conversion in Ads Manager. Ads Manager&#39;s own Conversions page walks you through it in four steps: create a data source, create a conversion event, implement and log the event, and link the event to a campaign.
      </P>
      <P>
        If the pixel is not installed yet, start with the <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">Tag Manager pixel install guide</A>. If it is installed but you are not sure it works, our <A href="/blog/how-to-check-chatgpt-ads-pixel-is-working">six-check pixel test</A> proves it end to end. OpenAI also recommends turning on <A href="/blog/chatgpt-ads-automatic-advanced-matching">automatic advanced matching</A>, which helps it match conversions to the people who saw your ads.
      </P>
      <InlineCTA href={CHECKER} label="Check your pixel setup free">
        Before you build the campaign, confirm the event exists. The free ChatGPT Ads Pixel Checker reads your landing page and Google Tag Manager container and lists every standard and custom event set up, so you can see whether the event you plan to optimize toward is actually there.
      </InlineCTA>

      <H2 id="which-event">Which conversion event should B2B optimize for?</H2>
      <P>
        <Strong>Optimize toward the deepest standard event that still fires often.</Strong> Deeper events sit closer to revenue but happen less, and OpenAI warns that events which happen too rarely make performance harder to evaluate. It does not say how rare is too rare.
      </P>
      <DataTable
        title="The B2B-relevant standard events, from shallow to deep"
        note="Definitions from OpenAI's developer documentation. Volume is relative, for a typical B2B funnel."
        headers={["Event", "OpenAI's definition", "B2B use", "Volume"]}
        rows={eventRows}
        minWidth={720}
      />
      <P>
        Ads Manager lists 12 standard events in all: the six above, two app events (App installed and App opened), and four ecommerce events such as Checkout started and Order created. <A href="https://developers.openai.com/ads/supported-events">OpenAI&#39;s event reference</A> gives the full definitions.
      </P>
      <P>
        For a sales-led company, <Strong>Appointment scheduled</Strong> is the event closest to pipeline: OpenAI defines it as booking a meeting, demo, or consultation. For a product-led company, <Strong>Trial started</Strong> plays the same role. If your demo or trial volume is thin, step back to <Strong>Lead created</Strong>, which fires more often and still carries buying intent.
      </P>
      <P>
        How thin is thin? OpenAI publishes no minimum. For a sense of scale, <A href="https://www.facebook.com/business/help/112167992830700">Meta says</A> its ad sets usually leave the learning phase after about 50 results in a week. That is Meta&#39;s system, not OpenAI&#39;s, but a campaign producing four demos a month is giving any optimizer very little to learn from. Track several stages of your funnel (our <A href={HUB}>conversion tracking guide</A> maps events to funnel types), and point each campaign at one of them.
      </P>
      <PullQuote>
        Pick the deepest event that still happens every week. Go deeper than that and the system is guessing.
      </PullQuote>

      <H2 id="impressions-or-clicks">Impressions or clicks: choosing how you are billed</H2>
      <P>
        <Strong>Impression billing pays per thousand impressions and lets the system chase conversions that follow a view as well as a click. Click billing pays per valid click and optimizes only toward conversions after a click.</Strong> OpenAI favors impressions; for B2B, the better first choice depends on what you can verify.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-conversions-objective-impressions-billing.png"
        title="Ads Manager / How you're charged"
        alt="ChatGPT Ads Manager set to the Conversions objective with Lead created as the event and How you're charged set to Impressions (CPM)."
        caption="After you choose Conversions and an event, the How you're charged box appears with Impressions (CPM) pre-selected."
      />
      <P>
        The box&#39;s own text makes OpenAI&#39;s case: impressions optimize for more conversions, including those driven by views, and selecting only clicks will limit your conversions. The reasoning holds. A buyer who reads your ad in ChatGPT and books a demo the next morning never clicked, and only impression billing lets delivery learn from that person.
      </P>
      <DataTable
        title="Impressions or clicks for a Conversions campaign"
        note="Per OpenAI's help center and Ads Manager, October 17, 2026."
        headers={["", "Impressions (CPM)", "Clicks (CPC)"]}
        rows={billingRows}
      />
      <P>
        The catch for B2B is verification. A view-driven conversion arrives with no click, so it carries none of your <A href="/glossary/utm-parameters">UTM parameters</A>, and your CRM cannot tie it to ChatGPT. OpenAI&#39;s reporting gives view-through credit for one day at most. That is the <A href="/glossary/attribution-gap">attribution gap</A> in its purest form: the platform says the ad worked, and nothing on your side can confirm it.
      </P>
      <P>
        So start on <Strong>Clicks</Strong> while you build a baseline your CRM can check, then clone the campaign onto <Strong>Impressions</Strong> once you trust the event and want more volume. That is also the one switch OpenAI supports by cloning; going from impressions back to clicks means a new campaign.
      </P>

      <H2 id="step-by-step">How to set up the campaign, step by step</H2>
      <P>
        <Strong>With the event live, the build takes seven steps, six of them inside one wizard.</Strong> These steps follow Ads Manager&#39;s New campaign wizard as it appeared on October 17, 2026, built with a test campaign that was never saved or launched.
      </P>

      <H3>1. Confirm your conversion event reaches OpenAI</H3>
      <P>
        Open the Conversions page and check that your data source shows recent events and that the event you want is set up as a conversion. If it is not, nothing later in the wizard will offer it.
      </P>

      <H3>2. Create a campaign and choose Conversions</H3>
      <P>
        Click Create, name the campaign, and set the Objective. In our account the menu read <Strong>Reach, Clicks, Conversions</Strong>; OpenAI&#39;s help center calls the first option Views. To move an existing campaign onto Conversions, clone it instead, since an objective cannot change in place.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-campaign-objective-reach-clicks-conversions.png"
        title="Ads Manager / Objective"
        alt="OpenAI's ChatGPT Ads Manager campaign setup with the objective menu open, showing Reach, Clicks and Conversions, and Billed by impressions below."
        caption="The Objective menu in the New campaign wizard. Once a billing option is chosen, the box collapses to a one-line summary."
      />

      <H3>3. Pick the conversion event</H3>
      <P>
        The Conversion event menu lists only events already set up as conversions, each labelled with its data source, plus a shortcut to create a new one. This choice is permanent for the campaign, so make it with the table above in front of you.
      </P>

      <H3>4. Choose how you&#39;re charged</H3>
      <P>
        Pick Impressions (CPM) or Clicks (CPC) in the How you&#39;re charged box. Neither the billing option nor the objective can be changed in place afterwards.
      </P>

      <H3>5. Set locations, budget and dates</H3>
      <P>
        Choose your markets, a daily budget and a schedule. OpenAI&#39;s advice is to give the campaign enough time and budget to gather meaningful data. Read the budget note carefully: when we entered a $100 daily budget, Ads Manager explained that the maximum daily spend is $200 and the maximum seven-day spend is $700. Single days can run to double, while the week stays at seven times your daily budget. The <A href="/blog/chatgpt-ads-cost">ChatGPT Ads cost guide</A> lists the per-campaign daily minimum for each billing currency.
      </P>

      <H3>6. Build the ad group and set the bid</H3>
      <P>
        Choose a bid strategy (the next section covers the choice), then fill in Tracking parameters. Ads Manager supports placeholders such as <Code>{"{campaign_id}"}</Code>, <Code>{"{ad_id}"}</Code> and <Code>{"{oppref}"}</Code>; add your UTMs, with <Code>utm_source=chatgpt</Code> and <Code>utm_medium=cpc</Code>, so every click reaches GA4 and your CRM labelled. Our guide to <A href="/blog/how-to-track-chatgpt-ads-in-ga4">tracking ChatGPT Ads in GA4</A> shows what arrives on the analytics side.
      </P>

      <H3>7. Add ads, review and launch</H3>
      <P>
        Create the ads, check the review screen, and launch. If you stop partway, know that Save as draft keeps the structure but not the money: Ads Manager warns that campaign budgets aren&#39;t saved with drafts. For the ad side of the build, the <A href="/blog/how-to-set-up-chatgpt-ad-campaign">full campaign setup walkthrough</A> covers creative and review.
      </P>

      <H2 id="bid-cap">Autobid or a manual bid cap</H2>
      <P>
        <Strong>Autobid adjusts bids to get the most conversions from your budget, and it was the starting setting on new ad groups in our account. Manual: Max bid instead sets a conversion-optimized bid cap: the most you are willing to bid for one conversion.</Strong> For most B2B accounts, the manual cap is the better start.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-conversion-optimized-bid-cap-audience-adjustments.png"
        title="Ads Manager / Ad group bid"
        alt="ChatGPT Ads Manager manual bid with a conversion-optimized bid cap billed per impression, and audience bid adjustments under Advanced."
        caption="Manual: Max bid on a Conversions ad group. The $820 was pre-filled by Ads Manager in our account; it is not a recommendation."
      />
      <P>
        The field&#39;s helper text explains the mechanics: Ads Manager uses the predicted conversion rate to turn your cap into a per-impression auction bid, or a per-click bid if you chose click billing. OpenAI adds that your actual cost per conversion is determined by the auction, and that there is no recommended bid amount at this time.
      </P>
      <P>
        That last line matters, because the field does not open empty. In our account it pre-filled <Strong>$820.00</Strong> with a &ldquo;Strong Delivery&rdquo; rating. OpenAI does not explain where that figure comes from, so treat it as a number to replace, not guidance. The honest way to set a cap is from what a conversion is worth to you.
      </P>
      <BidCapMath />
      <P>
        For this hypothetical AI marketing platform, a booked demo is worth about $900 in first-year revenue, and the most it should pay is $300. A cap of $820 would tell the auction it will pay nearly the full first-year value of a demo. If it optimized toward Lead created instead, the same math needs one more step: if 40% of leads book a demo, a lead is worth $120 at the same target. Either way it pays at most $4,000 in ads per customer, and a customer who stays three years brings in nine times that in revenue, a healthy ratio by B2B standards.
      </P>
      <P>
        Manual bidding unlocks one more control. Under Advanced, <Strong>Audience bid adjustments</Strong> apply a multiplier when a viewer belongs to an audience you choose. They exist only on Manual: Max bid, and they need a published <A href="/glossary/custom-audiences">custom audience</A>, which has its own <A href="/blog/chatgpt-ads-custom-audiences-minimum">B2B size problem</A>. Our post on <A href="/blog/chatgpt-ads-maximize-results-b2b">automatic bidding for B2B</A> explains why a volume-seeking default suits thin, uneven B2B conversions poorly.
      </P>
      <PullQuote>
        The bid cap is the one field that asks what a conversion is worth to you. Answer it with your own numbers, not the number in the box.
      </PullQuote>

      <H2 id="reading-results">Reading the results without fooling yourself</H2>
      <P>
        <Strong>Judge a conversion campaign on cost per conversion that your CRM can confirm, after enough time for conversions to arrive.</Strong> OpenAI says attributed conversions take 24 to 48 hours to appear in reporting, so the first two days can look worse than they are.
      </P>
      <P>
        Know that the platform keeps two separate sets of attribution windows. The first is set on each conversion event and steers delivery: when you create one, the click-through window defaults to 30 days and the view-through window to 1 day, and Ads Manager&#39;s tooltip notes that this window is used for optimization while reporting windows are configured separately.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-conversion-event-optimization-windows.png"
        title="Ads Manager / New conversion event"
        alt="ChatGPT Ads Manager conversion event settings with a 30-day click-through and 1-day view-through window used for optimization."
        caption="The conversion event dialog. These windows guide optimization, not the numbers in your reports."
      />
      <P>
        The second set lives in your report columns: a 7, 14 or 30-day click window and a 0 or 1-day view window. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001214-measure-results">reporting documentation</A> says changing them affects reporting only, not optimization, bidding or billing, and that attribution is last-touch.
      </P>
      <ScreenshotFrame
        src="/screenshots/chatgpt-ads-manager/chatgpt-ads-manager-reporting-attribution-windows-columns.png"
        title="Ads Manager / Edit columns"
        alt="ChatGPT Ads Manager column settings with conversion metrics and reporting attribution windows of 7, 14 or 30 days click and 0 or 1 day view."
        caption="Edit columns: pick Click-through conversions and View-through conversions separately, and set the reporting windows."
      />
      <P>
        With the view window at 1 day, the Conversions total includes view-through conversions. Add the <Strong>Click-through conversions</Strong> and <Strong>View-through conversions</Strong> columns side by side, and report the click-through figure when you mean what your clicks produced. Then reconcile against the CRM: count records with <Code>utm_source=chatgpt</Code> that reached the same stage. For a B2B cycle measured in months, the final verdict comes from pipeline, as our <A href="/blog/how-to-measure-roi-for-chatgpt-ads">ROI measurement framework</A> and the <A href="/glossary/chatgpt-ads-roas-stack">ROAS Stack</A> lay out.
      </P>

      <H2 id="mistakes">Mistakes that waste a conversion campaign</H2>
      <P>
        <Strong>The most expensive mistakes here are permanent, because three settings lock at launch.</Strong> Check this card before you click Launch.
      </P>
      <LockedAtLaunch />
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Optimizing toward page views.</Strong> It fires constantly, so the system learns fast, and it learns to find visitors rather than buyers.</Li>
        <Li><Strong>Choosing an event that almost never fires.</Strong> Subscription created sits closest to revenue, and for a sales-led company it is the event the system can least learn from.</Li>
        <Li><Strong>Accepting the pre-filled bid.</Strong> Replace it with a cap from your own funnel math.</Li>
        <Li><Strong>Reading the blended total as click performance.</Strong> With view-through on, Conversions mixes clicks and views.</Li>
        <Li><Strong>Judging on day one.</Strong> Conversions lag by up to 48 hours, and OpenAI advises waiting for enough volume before large changes.</Li>
        <Li><Strong>Trusting a draft with the budget.</Strong> Budgets are not saved with drafts; re-enter them before launch.</Li>
      </ul>
      <P>
        If ChatGPT Ads is new to your team, the <A href="/blog/chatgpt-search-ads-b2b">early B2B results</A> are worth reading before you commit a quarter&#39;s budget to one event.
      </P>

      {/* ── HowTo + FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to set up a ChatGPT Ads conversion campaign",
            step: howToSteps.map(([name, text], i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name,
              text,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href={HUB}>ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/how-to-check-chatgpt-ads-pixel-is-working">How to Check Your ChatGPT Ads Pixel Is Working</A></Li>
        <Li><A href="/blog/chatgpt-ads-maximize-results-b2b">ChatGPT Ads Now Bids for You by Default. B2B Should Opt Out.</A></Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)</A></Li>
        <Li><A href="/blog/how-to-measure-roi-for-chatgpt-ads">How to Measure ROI for ChatGPT Ads: The 2026 ROAS Framework for B2B Marketers</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-set-up-chatgpt-ads-conversion-campaigns",
  title: "How to Set Up a ChatGPT Ads Conversion Campaign for B2B",
  seoTitle: "How to Set Up a ChatGPT Ads Conversion Campaign for B2B",
  excerpt:
    "A ChatGPT Ads Conversions campaign never bills per conversion, and its event and billing option lock at launch. How B2B teams choose the event, the billing and the bid, with real Ads Manager screens.",
  metaDescription:
    "Set up a ChatGPT Ads Conversions campaign for B2B: pick the right event, choose impression or click billing, and set a bid cap from your own numbers.",
  date: "2026-10-19",
  readTime: "10 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "Conversions Objective", "Conversion Optimization", "Impression Billing", "Bid Cap", "B2B ChatGPT Ads", "HowTo"],
  fiveA: "ads",
  Content,
};

export default post;
